import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
  Upload,
  Download,
  CheckCircle2,
  AlertTriangle,
  FileSpreadsheet,
  RefreshCw,
  Search,
  Check,
  X,
  History,
  ArrowRight,
  ShieldAlert,
  Loader2,
  DollarSign,
  Users,
  FileCheck2,
  BarChart3,
  CreditCard
} from 'lucide-react';
import {
  downloadTemplate,
  validateUpload,
  processPayments,
  getProgress,
  downloadSettlementReport,
  getHistory
} from '../../../api/crm/customers/customerPaymentBulkService';
import {
  validateKpiUpload,
  processKpiUpload
} from '../../../api/crm/customers/customerKpiBulkService';
import { formatDate } from '../../../utils/stringUtils';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';

export default function CustomerPaymentBulkPage() {
  const [activeTab, setActiveTab] = useState('upload'); // 'upload' | 'history'
  const [uploadMode, setUploadMode] = useState('kpi'); // 'kpi' | 'invoice'

  // File & Validation States
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  // Table Filter & Search
  const [rowSearchTerm, setRowSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL'); // 'ALL' | 'VALID' | 'INVALID'

  // Processing States
  const [isProcessing, setIsProcessing] = useState(false);
  const [progressData, setProgressData] = useState(null);
  const [settlementResult, setSettlementResult] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // History States
  const [historyBatches, setHistoryBatches] = useState([]);
  const [historyPagination, setHistoryPagination] = useState({ page: 1, limit: 10, totalPages: 1 });
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const fileInputRef = useRef(null);
  const progressPollIntervalRef = useRef(null);

  // Load audit history when switching tab
  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory(1);
    }
  }, [activeTab]);

  // Clean up polling interval
  useEffect(() => {
    return () => {
      if (progressPollIntervalRef.current) {
        clearInterval(progressPollIntervalRef.current);
      }
    };
  }, []);

  const fetchHistory = async (page = 1, limit = historyPagination.limit || 10) => {
    try {
      setIsLoadingHistory(true);
      const res = await getHistory(page, limit);
      setHistoryBatches(res.data || []);
      setHistoryPagination(res.pagination || { page: 1, limit: 10, totalPages: 1 });
    } catch (err) {
      console.error('History fetch error:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setErrorMsg('');
      await downloadTemplate();
    } catch (err) {
      setErrorMsg(err.message || 'Failed to download template');
    }
  };

  const handleFileSelect = (file) => {
    setErrorMsg('');
    setValidationResult(null);
    setSettlementResult(null);

    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      setErrorMsg('Invalid file format. Please upload the official Excel (.xlsx) template.');
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleRunValidation = async () => {
    if (!selectedFile) return;

    try {
      setIsValidating(true);
      setErrorMsg('');
      const data = uploadMode === 'kpi'
        ? await validateKpiUpload(selectedFile)
        : await validateUpload(selectedFile);
      setValidationResult(data);
    } catch (err) {
      setErrorMsg(err.message || 'Validation failed');
    } finally {
      setIsValidating(false);
    }
  };

  const handleStartProcessing = async () => {
    setShowConfirmModal(false);
    if (!validationResult || !validationResult.batchId) return;

    const batchId = validationResult.batchId;

    try {
      setIsProcessing(true);
      setErrorMsg('');

      if (uploadMode === 'kpi') {
        const result = await processKpiUpload(batchId);
        setSettlementResult(result);
      } else {
        setProgressData({
          totalRows: validationResult.totalRows,
          processedRows: 0,
          percentage: 0,
          status: 'PROCESSING'
        });

        // Start polling interval
        progressPollIntervalRef.current = setInterval(async () => {
          try {
            const prog = await getProgress(batchId);
            setProgressData(prog);
            if (prog.status === 'COMPLETED' || prog.status === 'FAILED') {
              clearInterval(progressPollIntervalRef.current);
            }
          } catch (pollErr) {
            console.error('Poll error:', pollErr);
          }
        }, 1000);

        const result = await processPayments(batchId);
        setSettlementResult(result);
      }
    } catch (err) {
      setErrorMsg(err.message || 'Processing failed');
    } finally {
      if (progressPollIntervalRef.current) {
        clearInterval(progressPollIntervalRef.current);
      }
      setIsProcessing(false);
    }
  };

  const handleDownloadSettlementReport = async () => {
    if (!validationResult?.batchId) return;
    try {
      await downloadSettlementReport(validationResult.batchId);
    } catch (err) {
      setErrorMsg(err.message || 'Failed to download settlement report');
    }
  };

  /**
   * Download filtered rows (Valid or Invalid) in exact 3-column template format
   * (Customer ID, Customer Name, Outstanding Amount) using user-entered data
   */
  const handleDownloadFilteredRows = (filterType) => {
    try {
      const rowsToExport = (validationResult?.rows || []).filter((r) => {
        if (filterType === 'VALID') return r.isValid;
        if (filterType === 'INVALID') return !r.isValid;
        return true;
      });

      if (!rowsToExport || rowsToExport.length === 0) {
        setErrorMsg(`No ${filterType.toLowerCase()} records to download.`);
        return;
      }

      // Exact 3-column template format so user can fix and re-upload immediately
      const exportData = rowsToExport.map((r) => ({
        'Customer ID': r.customerId || '',
        'Customer Name': r.customerName || '',
        'Outstanding Amount': r.uploadedOutstanding != null ? r.uploadedOutstanding : ''
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      worksheet['!cols'] = [
        { wch: 18 },
        { wch: 32 },
        { wch: 22 }
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

      const prefix = filterType === 'VALID' ? 'Valid_Customers' : 'Invalid_Customers';
      const modeSuffix = uploadMode === 'kpi' ? 'KPI' : 'Payment';
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const dateStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

      XLSX.writeFile(workbook, `${prefix}_${modeSuffix}_${dateStr}.xlsx`);
    } catch (err) {
      console.error('Download filtered records error:', err);
      setErrorMsg(err.message || 'Failed to download records');
    }
  };

  const resetUploadState = () => {
    setSelectedFile(null);
    setValidationResult(null);
    setSettlementResult(null);
    setProgressData(null);
    setErrorMsg('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Filter parsed rows in validation preview table
  const filteredRows = (validationResult?.rows || []).filter((row) => {
    const matchesSearch =
      String(row.customerId || '').toLowerCase().includes(rowSearchTerm.toLowerCase()) ||
      String(row.customerName || '').toLowerCase().includes(rowSearchTerm.toLowerCase());

    if (statusFilter === 'VALID') return matchesSearch && row.isValid;
    if (statusFilter === 'INVALID') return matchesSearch && !row.isValid;
    return matchesSearch;
  });

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      {/* Header Bar */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
        <div>
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-emerald-50 rounded-lg text-emerald-600">
              <FileSpreadsheet className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Customer Payment Synchronization</h1>
              <p className="text-sm text-slate-500">
                Bulk upload accounting software payment files (.xlsx) to update CRM balances and settle invoices FIFO
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={handleDownloadTemplate}
            className="flex items-center space-x-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 rounded-lg text-xs font-semibold transition shadow-sm"
          >
            <Download className="h-3.5 w-3.5 text-emerald-600" />
            <span>Download Template</span>
          </button>

          <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200">
            <button
              onClick={() => setActiveTab('upload')}
              className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm font-medium transition ${activeTab === 'upload' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
            >
              <Upload className="h-4 w-4" />
              <span>Bulk Upload</span>
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm font-medium transition ${activeTab === 'history' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
            >
              <History className="h-4 w-4" />
              <span>Audit History</span>
            </button>
          </div>
        </div>
      </div>

      {/* Global Error Banner */}
      {errorMsg && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-3 text-red-700">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-sm">Action Error</p>
            <p className="text-sm">{errorMsg}</p>
          </div>
          <button onClick={() => setErrorMsg('')} className="text-red-400 hover:text-red-600">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* MAIN TAB: BULK UPLOAD */}
      {activeTab === 'upload' && (
        <div className="space-y-6">
          {/* STEP 0: UPLOAD MODE SELECTOR */}
          {!validationResult && !settlementResult && (
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => {
                    setUploadMode('kpi');
                    resetUploadState();
                  }}
                  className={`flex-1 sm:flex-initial flex items-center justify-center space-x-2 px-3.5 py-1.5 rounded-md text-xs font-semibold transition ${
                    uploadMode === 'kpi'
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <BarChart3 className="h-3.5 w-3.5" />
                  <span>KPI Payment Bulk Upload (Metrics Only)</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setUploadMode('invoice');
                    resetUploadState();
                  }}
                  className={`flex-1 sm:flex-initial flex items-center justify-center space-x-2 px-3.5 py-1.5 rounded-md text-xs font-semibold transition ${
                    uploadMode === 'invoice'
                      ? 'bg-white text-emerald-700 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <CreditCard className="h-3.5 w-3.5" />
                  <span>Invoice Settlement (FIFO Ledger)</span>
                </button>
              </div>
              <div className="hidden sm:block border-l border-slate-200 pl-4 py-0.5">
                <p className="text-xs text-slate-500">
                  {uploadMode === 'kpi'
                    ? 'Updates customer 360 KPI outstanding balances directly without touching invoice ledgers.'
                    : 'Settles open invoices FIFO and records formal transaction payment ledger entries.'}
                </p>
              </div>
            </div>
          )}

          {/* STEP 1: FILE SELECTION / UPLOAD ZONE */}
          {!validationResult && !settlementResult && (
            <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <p className="text-sm text-slate-500">
                  {uploadMode === 'kpi' ? (
                    <>
                      Upload your Excel export containing <span className="font-semibold text-slate-700">Customer ID</span>,{' '}
                      <span className="font-semibold text-slate-700">Customer Name</span>, and{' '}
                      <span className="font-semibold text-slate-700">Uploaded Outstanding</span> to update Customer 360 KPI metrics directly.{' '}
                      <span className="text-blue-600 font-medium">Invoices and ledgers are not affected.</span>
                    </>
                  ) : (
                    <>
                      Upload your accounting export containing <span className="font-semibold text-slate-700">Customer ID</span>,{' '}
                      <span className="font-semibold text-slate-700">Customer Name</span>, and{' '}
                      <span className="font-semibold text-slate-700">Uploaded Outstanding</span>. Only <span className="font-semibold text-emerald-600">.xlsx</span> files (Max 2,000 rows) are accepted.
                    </>
                  )}
                </p>
                <button
                  onClick={handleDownloadTemplate}
                  className="flex items-center space-x-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 rounded-lg text-xs font-semibold transition whitespace-nowrap shadow-sm"
                >
                  <Download className="h-3.5 w-3.5 text-emerald-600" />
                  <span>Download Template</span>
                </button>
              </div>

              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragOver(true);
                }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition ${isDragOver
                  ? uploadMode === 'kpi' ? 'border-blue-500 bg-blue-50/50' : 'border-emerald-500 bg-emerald-50/50'
                  : selectedFile
                    ? uploadMode === 'kpi' ? 'border-blue-300 bg-blue-50/20' : 'border-emerald-300 bg-emerald-50/20'
                    : 'border-slate-300 hover:border-slate-400 bg-slate-50/50'
                  }`}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".xlsx"
                  onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                  className="hidden"
                />

                <div className={`mx-auto w-14 h-14 ${uploadMode === 'kpi' ? 'bg-blue-100 text-blue-600' : 'bg-emerald-100 text-emerald-600'} rounded-full flex items-center justify-center mb-4`}>
                  {selectedFile ? <FileCheck2 className="h-7 w-7" /> : <Upload className="h-7 w-7" />}
                </div>

                {selectedFile ? (
                  <div>
                    <p className="text-lg font-semibold text-slate-900">{selectedFile.name}</p>
                    <p className="text-sm text-slate-500 mt-1">
                      {(selectedFile.size / 1024).toFixed(1)} KB • Ready for {uploadMode === 'kpi' ? 'KPI validation' : 'validation'}
                    </p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                      }}
                      className="mt-3 text-xs text-red-600 hover:underline font-medium"
                    >
                      Remove File
                    </button>
                  </div>
                ) : (
                  <div>
                    <p className="text-base font-semibold text-slate-800">
                      Drag & Drop your Excel file here, or <span className={`${uploadMode === 'kpi' ? 'text-blue-600' : 'text-emerald-600'} underline`}>Browse</span>
                    </p>
                    <p className="text-xs text-slate-400 mt-2">Only Microsoft Excel (.xlsx) formats supported</p>
                  </div>
                )}
              </div>

              {selectedFile && (
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={handleRunValidation}
                    disabled={isValidating}
                    className={`flex items-center space-x-1.5 px-4 py-2 ${uploadMode === 'kpi' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'} text-white font-medium text-xs rounded-lg shadow transition disabled:opacity-50`}
                  >
                    {isValidating ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        <span>{uploadMode === 'kpi' ? 'Validating File & Total Amounts...' : 'Validating File & CRM Balances...'}</span>
                      </>
                    ) : (
                      <>
                        <span>{uploadMode === 'kpi' ? 'Validate KPI Data' : 'Validate File'}</span>
                        <ArrowRight className="h-3.5 w-3.5" />
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* STEP 2: READ-ONLY VALIDATION SUMMARY & PREVIEW */}
          {validationResult && !settlementResult && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Total Upload Rows</span>
                    <Users className="h-5 w-5 text-slate-400" />
                  </div>
                  <p className="text-2xl font-bold text-slate-900 mt-2">{validationResult.totalRows}</p>
                </div>

                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">Valid Customers</span>
                    <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                  </div>
                  <p className="text-2xl font-bold text-emerald-600 mt-2">{validationResult.validRowsCount}</p>
                </div>

                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-red-600 uppercase tracking-wider">Invalid Rows</span>
                    <ShieldAlert className="h-5 w-5 text-red-500" />
                  </div>
                  <p className="text-2xl font-bold text-red-600 mt-2">{validationResult.invalidRowsCount}</p>
                </div>

                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-blue-600 uppercase tracking-wider">
                      {uploadMode === 'kpi' ? 'Total KPI Outstanding' : 'Total Payment Calculated'}
                    </span>
                    <DollarSign className="h-5 w-5 text-blue-500" />
                  </div>
                  <p className="text-2xl font-bold text-blue-600 mt-2">
                    ₹{(uploadMode === 'kpi'
                      ? (validationResult.summary?.totalKpiOutstanding || 0)
                      : (validationResult.summary?.totalCalculatedPayment || 0)
                    ).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>

              {/* Action Toolbar */}
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center space-x-3 flex-1">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search Customer ID or Name..."
                      value={rowSearchTerm}
                      onChange={(e) => setRowSearchTerm(e.target.value)}
                      className="w-full pl-9 pr-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div className="flex space-x-1 bg-slate-100 p-1 rounded-lg text-xs font-medium">
                    <button
                      onClick={() => setStatusFilter('ALL')}
                      className={`px-3 py-1 rounded-md transition ${statusFilter === 'ALL' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}
                    >
                      All ({validationResult.rows?.length || 0})
                    </button>
                    <button
                      onClick={() => setStatusFilter('VALID')}
                      className={`px-3 py-1 rounded-md transition ${statusFilter === 'VALID' ? 'bg-emerald-500 text-white' : 'text-emerald-700'}`}
                    >
                      Valid ({validationResult.validRowsCount})
                    </button>
                    <button
                      onClick={() => setStatusFilter('INVALID')}
                      className={`px-3 py-1 rounded-md transition ${statusFilter === 'INVALID' ? 'bg-red-500 text-white' : 'text-red-700'}`}
                    >
                      Invalid ({validationResult.invalidRowsCount})
                    </button>
                  </div>

                  {statusFilter === 'VALID' && validationResult.validRowsCount > 0 && (
                    <button
                      type="button"
                      onClick={() => handleDownloadFilteredRows('VALID')}
                      className="flex items-center space-x-1.5 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-lg text-xs font-semibold transition shadow-sm whitespace-nowrap"
                      title="Download valid records in 3-column template format (.xlsx)"
                    >
                      <Download className="h-3.5 w-3.5 text-emerald-600" />
                      <span>Download Valid ({validationResult.validRowsCount})</span>
                    </button>
                  )}

                  {statusFilter === 'INVALID' && validationResult.invalidRowsCount > 0 && (
                    <button
                      type="button"
                      onClick={() => handleDownloadFilteredRows('INVALID')}
                      className="flex items-center space-x-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-lg text-xs font-semibold transition shadow-sm whitespace-nowrap"
                      title="Download invalid records in template format (.xlsx) to update and upload again"
                    >
                      <Download className="h-3.5 w-3.5 text-red-600" />
                      <span>Download Invalid ({validationResult.invalidRowsCount})</span>
                    </button>
                  )}
                </div>

                <div className="flex items-center space-x-3">
                  <button
                    onClick={resetUploadState}
                    className="px-4 py-2 border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium rounded-lg transition"
                  >
                    Cancel & Re-upload
                  </button>

                  <button
                    onClick={() => setShowConfirmModal(true)}
                    disabled={validationResult.validRowsCount === 0 || isProcessing}
                    className={`flex items-center space-x-2 px-6 py-2 ${uploadMode === 'kpi' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'} text-white text-sm font-semibold rounded-lg shadow transition disabled:opacity-50`}
                  >
                    <Check className="h-4 w-4" />
                    <span>
                      {uploadMode === 'kpi'
                        ? `Update KPI for ${validationResult.validRowsCount} Customers`
                        : `Process ${validationResult.validRowsCount} Valid Payments`}
                    </span>
                  </button>
                </div>
              </div>

              {/* Progress Bar (during execution) */}
              {isProcessing && progressData && (
                <div className="bg-white p-6 rounded-xl border border-emerald-200 shadow-md space-y-3">
                  <div className="flex justify-between items-center text-sm font-semibold">
                    <span className="text-slate-800 flex items-center space-x-2">
                      <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
                      <span>Processing Payment Allocations... ({progressData.processedRows} / {progressData.totalRows} rows)</span>
                    </span>
                    <span className="text-emerald-600 font-mono">{progressData.percentage}%</span>
                  </div>

                  <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-emerald-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${progressData.percentage}%` }}
                    ></div>
                  </div>

                  <div className="flex justify-between text-xs text-slate-500 font-mono pt-1">
                    <span>Successful: {progressData.successRows || 0}</span>
                    <span>Failed: {progressData.failedRows || 0}</span>
                    <span>Status: {progressData.status}</span>
                  </div>
                </div>
              )}

              {/* Preview Table */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="max-h-[480px] overflow-y-auto">
                  <table className="w-full text-left text-sm border-collapse">
                    <thead className="sticky top-0 bg-slate-100 border-b border-slate-200 text-slate-600 font-semibold text-xs uppercase tracking-wider">
                      <tr>
                        <th className="p-3">Row</th>
                        <th className="p-3">Customer ID</th>
                        <th className="p-3">Customer Name</th>
                        {uploadMode === 'kpi' ? (
                          <>
                            <th className="p-3 text-right">Customer Total Amount</th>
                            <th className="p-3 text-right">New KPI Outstanding</th>
                          </>
                        ) : (
                          <>
                            <th className="p-3 text-right">Current CRM Balance</th>
                            <th className="p-3 text-right">Uploaded Outstanding</th>
                            <th className="p-3 text-right">Calculated Payment</th>
                          </>
                        )}
                        <th className="p-3 text-center">Status</th>
                        <th className="p-3">Validation Message / Reason</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredRows.length > 0 ? (
                        filteredRows.map((row) => (
                          <tr key={row.rowNumber} className={row.isValid ? 'hover:bg-slate-50' : 'bg-red-50/30 hover:bg-red-50/50'}>
                            <td className="p-3 text-slate-400 font-mono text-xs">{row.rowNumber}</td>
                            <td className="p-3 font-semibold text-slate-800 font-mono">{row.customerId}</td>
                            <td className="p-3 text-slate-700">{row.customerName || '-'}</td>
                            {uploadMode === 'kpi' ? (
                              <>
                                <td className="p-3 text-right font-mono text-slate-700">
                                  ₹{(row.customerTotalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </td>
                                <td className="p-3 text-right font-mono font-bold text-blue-600">
                                  ₹{(row.uploadedOutstanding || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="p-3 text-right font-mono text-slate-700">
                                  ₹{(row.currentCrmOutstanding || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </td>
                                <td className="p-3 text-right font-mono text-slate-700">
                                  ₹{(row.uploadedOutstanding || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </td>
                                <td className="p-3 text-right font-mono font-bold text-emerald-600">
                                  ₹{(row.calculatedPaymentAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </td>
                              </>
                            )}
                            <td className="p-3 text-center">
                              {row.isValid ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                                  VALID
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                                  INVALID
                                </span>
                              )}
                            </td>
                            <td className="p-3 text-xs text-slate-600">
                              {row.isValid ? (
                                <span className="text-emerald-600 font-medium">
                                  {uploadMode === 'kpi' ? 'Ready to update KPI' : 'Ready for payment allocation'}
                                </span>
                              ) : (
                                <span className="text-red-600 font-semibold">{row.reason || row.errorMessage || row.errors?.join('; ')}</span>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={uploadMode === 'kpi' ? 6 : 8} className="p-8 text-center text-slate-400">
                            No row records match your current filter
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: SETTLEMENT / KPI COMPLETED RESULTS */}
          {settlementResult && uploadMode === 'kpi' ? (
            <div className="bg-white p-8 rounded-xl border border-blue-200 shadow-sm space-y-6">
              <div className="flex items-center space-x-4 text-blue-600">
                <div className="p-3 bg-blue-100 rounded-full">
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Customer KPI Outstanding Updated</h2>
                  <p className="text-sm text-slate-500">
                    Successfully updated KPI outstanding for batch <span className="font-mono font-semibold">{settlementResult.batchId}</span>.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <span className="text-xs text-slate-400 font-semibold uppercase">Customers Updated</span>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {settlementResult.summary?.modifiedCustomersCount ?? settlementResult.summary?.validRows ?? 0}
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <span className="text-xs text-slate-400 font-semibold uppercase">Invalid Rows Skipped</span>
                  <p className="text-2xl font-bold text-red-600 mt-1">
                    {settlementResult.summary?.invalidRows ?? 0}
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <span className="text-xs text-slate-400 font-semibold uppercase">Total Rows in File</span>
                  <p className="text-2xl font-bold text-blue-600 mt-1">
                    {settlementResult.summary?.totalRows ?? 0}
                  </p>
                </div>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <p className="text-xs text-blue-800">
                  Customer 360 KPIs have been synchronized with the uploaded outstanding balances. Invoices and payment ledgers remained unaffected.
                </p>
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={resetUploadState}
                  className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-medium text-sm rounded-lg shadow transition"
                >
                  Upload Another File
                </button>
              </div>
            </div>
          ) : settlementResult && (
            <div className="bg-white p-8 rounded-xl border border-emerald-200 shadow-sm space-y-6">
              <div className="flex items-center space-x-4 text-emerald-600">
                <div className="p-3 bg-emerald-100 rounded-full">
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Bulk Payment Allocation Completed</h2>
                  <p className="text-sm text-slate-500">
                    Successfully processed payment synchronization for batch <span className="font-mono font-semibold">{settlementResult.batchId}</span>
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-2">
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <span className="text-xs text-slate-400 font-semibold uppercase">Total Customers Settled</span>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{settlementResult.processedCustomers || 0}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <span className="text-xs text-slate-400 font-semibold uppercase">Invoices Updated</span>
                  <p className="text-2xl font-bold text-emerald-600 mt-1">{settlementResult.updatedInvoicesCount || 0}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <span className="text-xs text-slate-400 font-semibold uppercase">Total Payment Applied</span>
                  <p className="text-2xl font-bold text-blue-600 mt-1">
                    ₹{(settlementResult.totalPaymentApplied || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>

              {settlementResult.customerSettlements && settlementResult.customerSettlements.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mt-4">
                  <div className="p-4 bg-slate-50 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-800 text-sm">Customer Settlement Breakdown</h3>
                  </div>
                  <div className="max-h-[320px] overflow-y-auto">
                    <table className="w-full text-left text-sm border-collapse">
                      <thead className="sticky top-0 bg-slate-100 border-b border-slate-200 text-slate-600 font-semibold text-xs uppercase tracking-wider">
                        <tr>
                          <th className="p-3">Customer ID</th>
                          <th className="p-3">Customer Name</th>
                          <th className="p-3 text-right">Payment Applied</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {settlementResult.customerSettlements.map((cs, idx) => (
                          <tr key={idx} className="hover:bg-slate-50">
                            <td className="p-3 font-mono font-semibold text-slate-800">{cs.customerId}</td>
                            <td className="p-3 text-slate-700">{cs.customerName}</td>
                            <td className="p-3 text-right font-mono font-bold text-emerald-600">
                              ₹{(cs.totalPaymentApplied || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between">
                <div>
                  <p className="font-semibold text-emerald-900 text-sm">Download Invoice Settlement Report</p>
                  <p className="text-xs text-emerald-700 mt-0.5">
                    Download temporary in-memory Excel report detailing exact updated invoices (1 row per invoice).
                  </p>
                </div>
                <button
                  onClick={handleDownloadSettlementReport}
                  className="flex items-center space-x-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-sm rounded-lg shadow transition"
                >
                  <Download className="h-4 w-4" />
                  <span>Download Settlement Report (.xlsx)</span>
                </button>
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={resetUploadState}
                  className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-medium text-sm rounded-lg shadow transition"
                >
                  Upload Another File
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB: AUDIT HISTORY */}
      {activeTab === 'history' && (
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-slate-900">Bulk Upload Audit Trail</h2>
            <button
              onClick={() => fetchHistory(historyPagination.page)}
              className="flex items-center space-x-1.5 text-xs text-slate-600 hover:text-slate-900 border px-3 py-1.5 rounded-md"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              <span>Refresh</span>
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead className="bg-slate-100 text-slate-600 font-semibold text-xs uppercase tracking-wider">
                <tr>
                  <th className="p-3">Batch ID</th>
                  <th className="p-3">File Name</th>
                  <th className="p-3">Uploaded By</th>
                  <th className="p-3 text-center">Status</th>
                  <th className="p-3 text-right">Total Rows</th>
                  <th className="p-3 text-right">Valid Rows</th>
                  <th className="p-3 text-right">Processed</th>
                  <th className="p-3">Created At</th>
                  <th className="p-3 text-center">Report</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoadingHistory ? (
                  <tr>
                    <td colSpan={9} className="p-8 text-center text-slate-400">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-emerald-600" />
                      Loading import audit history...
                    </td>
                  </tr>
                ) : historyBatches.length > 0 ? (
                  historyBatches.map((batch) => (
                    <tr key={batch.batchId} className="hover:bg-slate-50">
                      <td className="p-3 font-mono font-semibold text-slate-800">{batch.batchId}</td>
                      <td className="p-3 text-slate-700">{batch.fileName}</td>
                      <td className="p-3 text-slate-600">
                        {batch.uploadedByName || `${batch.uploadedBy?.firstName || ''} ${batch.uploadedBy?.lastName || ''}`.trim() || 'System'}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${batch.status === 'COMPLETED'
                          ? 'bg-emerald-100 text-emerald-800'
                          : batch.status === 'VALIDATED'
                            ? 'bg-blue-100 text-blue-800'
                            : batch.status === 'FAILED' || batch.status === 'VALIDATION_FAILED'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}>
                          {batch.status}
                        </span>
                      </td>
                      <td className="p-3 text-right font-mono text-slate-700">{batch.totalRows}</td>
                      <td className="p-3 text-right font-mono text-emerald-600">{batch.validRowsCount}</td>
                      <td className="p-3 text-right font-mono text-slate-700">{batch.processedRows || 0}</td>
                      <td className="p-3 text-xs text-slate-500">
                        {batch.createdAt ? formatDate(batch.createdAt, { includeTime: true }) : '-'}
                      </td>
                      <td className="p-3 text-center">
                        {batch.status === 'COMPLETED' ? (
                          <button
                            onClick={() => downloadSettlementReport(batch.batchId)}
                            className="inline-flex items-center space-x-1 px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded text-xs font-medium transition shadow-sm"
                            title="Download Invoice Settlement Report (.xlsx)"
                          >
                            <Download className="h-3 w-3 text-emerald-600" />
                            <span>Report</span>
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="p-8 text-center text-slate-400">
                      No bulk payment upload history recorded yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* AUDIT TRAIL PAGINATION CONTROLS */}
          <PaginationControls
            currentPage={historyPagination.page || 1}
            totalPages={historyPagination.totalPages || 1}
            totalItems={historyPagination.total || 0}
            limit={historyPagination.limit || 10}
            onPageChange={(page) => fetchHistory(page, historyPagination.limit || 10)}
            onLimitChange={(newLimit) => fetchHistory(1, newLimit)}
            hasPrevPage={(historyPagination.page || 1) > 1}
            hasNextPage={(historyPagination.page || 1) < (historyPagination.totalPages || 1)}
          />
        </div>
      )}

      {/* CONFIRMATION MODAL */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-4">
            <div className={`flex items-center space-x-3 ${uploadMode === 'kpi' ? 'text-blue-600' : 'text-amber-600'}`}>
              <div className={`p-2.5 ${uploadMode === 'kpi' ? 'bg-blue-100' : 'bg-amber-100'} rounded-full`}>
                <AlertTriangle className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">
                {uploadMode === 'kpi' ? 'Confirm KPI Outstanding Update' : 'Confirm Payment Allocation'}
              </h3>
            </div>

            {uploadMode === 'kpi' ? (
              <>
                <p className="text-sm text-slate-600 leading-relaxed">
                  You are about to update the KPI outstanding amount directly on the customer record for{' '}
                  <span className="font-bold text-slate-900">{validationResult?.validRowsCount} valid customer records</span> with total KPI outstanding of{' '}
                  <span className="font-bold text-blue-600">
                    ₹{(validationResult?.summary?.totalKpiOutstanding || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>.
                </p>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800 leading-relaxed">
                  <strong>Notice:</strong> This directly updates the Customer 360 Outstanding KPI card. No invoices or payment ledgers are modified.
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-600 leading-relaxed">
                You are about to execute payment allocations for{' '}
                <span className="font-bold text-slate-900">{validationResult?.validRowsCount} valid customer records</span> with total payment amount of{' '}
                <span className="font-bold text-emerald-600">
                  ₹{(validationResult?.summary?.totalCalculatedPayment || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>.
                This will update unpaid customer invoices FIFO and adjust CRM financial balances.
              </p>
            )}

            <div className="flex items-center justify-end space-x-3 pt-2">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleStartProcessing}
                className={`px-5 py-2 ${uploadMode === 'kpi' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'} text-white text-sm font-semibold rounded-lg shadow transition`}
              >
                {uploadMode === 'kpi' ? 'Confirm & Update KPIs' : 'Confirm & Process Payments'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
